import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api, { getErrorMessage } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, Phone, Loader2, Eye, EyeOff, User } from 'lucide-react';
import Logo from '../components/Logo';

export default function CompleteGoogleProfile() {
    const navigate = useNavigate();
    const { user, updateUser, updateToken } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [formData, setFormData] = useState({
        phone: '',
        password: '',
        confirmPassword: '',
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.phone.trim()) {
            toast.error('Telefone é obrigatório');
            return;
        }
        if (!formData.password || formData.password.length < 6) {
            toast.error('Senha deve ter no mínimo 6 caracteres');
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            toast.error('As senhas não coincidem');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/auth/complete-google-profile', {
                phone: formData.phone,
                password: formData.password,
            });

            const { user: updatedUser, token } = response.data;
            if (token) updateToken(token);
            if (updatedUser) updateUser(updatedUser);

            toast.success('Perfil completado com sucesso!');

            const userType = updatedUser?.user_type;
            if (['host', 'caregiver', 'driver', 'partner'].includes(userType)) {
                navigate('/provider', { replace: true });
            } else {
                navigate('/tutor', { replace: true });
            }
        } catch (error) {
            toast.error(getErrorMessage(error, 'Erro ao salvar perfil'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen hero-gradient flex items-center justify-center p-4 py-8" data-testid="complete-google-profile-page">
            <div className="w-full max-w-xl">
                <Card className="w-full shadow-xl border-0">
                    <CardHeader className="text-center pb-2">
                        <div className="flex justify-center mb-4">
                            <Logo size="lg" />
                        </div>
                        <CardTitle className="text-2xl font-bold font-['Nunito']">Criar conta</CardTitle>
                        <CardDescription>Informações pessoais</CardDescription>
                        <div className="flex justify-center gap-2 mt-4">
                            <div className="h-2 w-8 rounded-full bg-[#4A90B2]" />
                            <div className="h-2 w-8 rounded-full bg-[#4A90B2]" />
                            <div className="h-2 w-2 rounded-full bg-slate-200" />
                        </div>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Nome completo *</Label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <Input
                                        id="name"
                                        value={user?.name || ''}
                                        readOnly
                                        className="pl-10 bg-slate-50 text-slate-500 cursor-not-allowed"
                                        data-testid="google-profile-name"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email">E-mail *</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <Input
                                        id="email"
                                        type="email"
                                        value={user?.email || ''}
                                        readOnly
                                        className="pl-10 bg-slate-50 text-slate-500 cursor-not-allowed"
                                        data-testid="google-profile-email"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="phone">Telefone *</Label>
                                <div className="relative">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <Input
                                        id="phone"
                                        placeholder="(11) 99999-9999"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        className="pl-10 bg-white"
                                        required
                                        autoFocus
                                        data-testid="google-profile-phone"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">Senha *</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Mínimo 6 caracteres"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        className="pl-10 pr-10 bg-white"
                                        required
                                        data-testid="google-profile-password"
                                    />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirmar senha *</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <Input
                                        id="confirmPassword"
                                        type={showConfirm ? 'text' : 'password'}
                                        placeholder="Confirme sua senha"
                                        value={formData.confirmPassword}
                                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                        className="pl-10 pr-10 bg-white"
                                        required
                                        data-testid="google-profile-confirm-password"
                                    />
                                    <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                        {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="google-profile-submit">
                                {loading ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvando...</>
                                ) : 'Continuar'}
                            </Button>

                            <p className="text-center text-sm text-slate-500 mt-4">
                                Já tem uma conta? <a href="/login" className="text-[#4A90B2] hover:underline font-medium">Entrar</a>
                            </p>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
